import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = "/api";
const AuthContext = createContext(null);

function saveSession(data) {
  if (data.token) localStorage.setItem("auth_token", data.token);
  if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
  if (data.wedding_id && !localStorage.getItem("active_wedding_id")) {
    localStorage.setItem("active_wedding_id", data.wedding_id);
  }
}

function clearSession() {
  localStorage.removeItem("auth_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("active_wedding_id");
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshPending = useRef(null);

  const refreshSession = useCallback(async () => {
    if (refreshPending.current) return refreshPending.current;
    const refresh = localStorage.getItem("refresh_token");
    if (!refresh) return null;
    refreshPending.current = axios.post(API + "/auth/refresh", { refresh_token: refresh })
      .then(({ data }) => {
        if (!data.token) throw new Error("Session expired");
        saveSession(data);
        return data.token;
      })
      .catch(() => {
        clearSession();
        return null;
      })
      .finally(() => { refreshPending.current = null; });
    return refreshPending.current;
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      let token = localStorage.getItem("auth_token");
      if (!token) token = await refreshSession();
      if (!token) {
        setUser(null);
        return;
      }
      let response;
      try {
        response = await axios.get(API + "/auth/me", {
          headers: { Authorization: "Bearer " + token },
        });
      } catch (error) {
        if (error.response?.status !== 401) throw error;
        token = await refreshSession();
        if (!token) throw error;
        response = await axios.get(API + "/auth/me", {
          headers: { Authorization: "Bearer " + token },
        });
      }
      setUser(response.data);
      if (response.data.wedding_id && !localStorage.getItem("active_wedding_id")) {
        localStorage.setItem("active_wedding_id", response.data.wedding_id);
      }
    } catch {
      clearSession();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [refreshSession]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (result) => result,
      async (error) => {
        const original = error.config;
        if (error.response?.status === 401 &&
            original && !original._royalRetried &&
            typeof original.url === "string" &&
            original.url.startsWith(API) &&
            !original.url.includes("/auth/")) {
          original._royalRetried = true;
          const token = await refreshSession();
          if (token) {
            original.headers = { ...original.headers, Authorization: "Bearer " + token };
            return axios(original);
          }
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [refreshSession]);

  const login = async (email, password) => {
    const { data } = await axios.post(API + "/auth/login", { email, password });
    saveSession(data);
    setUser(data);
    return data;
  };

  const register = async (email, password, name, weddingTitle) => {
    const { data } = await axios.post(API + "/auth/register", {
      email, password, name, wedding_title: weddingTitle,
    });
    if (!data.requires_confirmation) {
      saveSession(data);
      setUser(data);
    }
    return data;
  };

  const loginWithGoogle = async () => {
    const { data } = await axios.get(API + "/auth/google");
    window.location.assign(data.url);
  };

  const completeOAuth = async () => {
    const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const error = fragment.get("error_description") || fragment.get("error");
    if (error) throw new Error(error);
    const token = fragment.get("access_token");
    const refresh = fragment.get("refresh_token");
    if (!token) throw new Error("Google sign-in did not return a session");
    saveSession({ token, refresh_token: refresh });
    window.history.replaceState({}, document.title, window.location.pathname);
    const { data } = await axios.get(API + "/auth/me", {
      headers: { Authorization: "Bearer " + token },
    });
    if (data.wedding_id && !localStorage.getItem("active_wedding_id")) {
      localStorage.setItem("active_wedding_id", data.wedding_id);
    }
    setUser(data);
    return data;
  };

  const logout = async () => {
    const token = localStorage.getItem("auth_token");
    clearSession();
    setUser(null);
    if (token) {
      try {
        await axios.post(API + "/auth/logout", {}, {
          headers: { Authorization: "Bearer " + token },
        });
      } catch {
        // Local tokens have already been removed.
      }
    }
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, loginWithGoogle, completeOAuth,
      logout, refreshSession, checkAuth,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
